import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  currentSpeakerAtom,
  currentVRMAtom,
  isProcessingAtom,
  isThinkingAtom,
  isTranscribingAtom,
  T2SEngineAtom,
  volumeAtom,
} from "~/Atom";
import handleSynthesize from "~/TTS/voicevox/VoiceSynthesis";
import {
  addDictionary,
  updateDictionary,
  deleteDictionary,
  Dict,
  DictionaryObject,
} from "~/TTS/voicevox/VoicevoxUtils";
import { defaultSpeakerInfo, getVRMName, LoppyInfo } from "~/vrm_utils/VRMUtils";
import { api } from "~/api";
import { useTranslation } from "react-i18next";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import { PlaySound } from "~/SoundUtils";
import { t2SEngines } from "~/Commons";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";

export type DictionaryFormMode = "create" | "edit";

export interface UseDictionaryOptions {
  mode: DictionaryFormMode;
  initialDict?: DictionaryObject | null;
  registeredDicts?: DictionaryObject[];
  onSaved?: () => void;
  onDeleted?: () => void;
  onCancel?: () => void;
}

/**
 * 入力値の検証
 *
 * @param word 言葉
 * @param kana 読み(カナ)
 * @returns [boolean, string] - 検証結果とメッセージ
 */
type TFunction = (k: string, opts?: Record<string, unknown>) => string;
const validateInputs = (word: string, kana: string, t: TFunction): [boolean, string] => {
  // 言葉と読み(カナ)の必須チェック
  if (!word) return [false, t("inputValidation", { attribute: t("dictionaryWord") })];
  if (!kana) return [false, t("inputValidation", { attribute: t("pronunciation") })];
  // 長音符(ー)のチェック
  if (/ー/.test(kana)) return [false, t("longVowelMark")];
  // 捨て仮名の連続チェック
  if (/[ァィゥェォヵㇰヶㇱㇲッㇳㇴㇵㇶㇷㇸㇹㇺャュョㇻㇼㇽㇾㇿヮ]{2}/.test(kana))
    return [false, t("consecutiveSmallKana")];
  // カタカナのチェック
  const katakanaRegex = /^[\u30A0-\u30FF\s]+$/;
  if (!katakanaRegex.test(kana))
    return [false, t("onlyKatakana", { attribute: t("pronunciation") })];
  return [true, ""];
};

/**
 * 読み(カナ)をモーラに分割する
 *
 * @param kana 読み(カナ)
 * @returns モーラ配列
 */
export const splitMoras = (kana: string): string[] => {
  if (!kana) return [];
  const chars = kana.match(/(?:[ァ-ヺ]|ー)/g) || [];
  const result: string[] = [];
  for (let i = 0; i < chars.length; i++) {
    let c = chars[i];
    if (
      i + 1 < chars.length &&
      /[ャュョァィゥェォ]/.test(chars[i + 1]) &&
      !/[ャュョァィゥェォ]/.test(chars[i])
    ) {
      c += chars[i + 1];
      i++;
    }
    result.push(c);
  }
  return result;
};

export const useDictionary = (opts: UseDictionaryOptions) => {
  const { mode, initialDict, registeredDicts = [], onSaved, onDeleted, onCancel } = opts;
  const [word, setWord] = useState("");
  const [kana, setKana] = useState("");
  const [priority, setPriority] = useState<number>(9);
  const [accent, setAccent] = useState<number>(1);
  const [targetId, setTargetId] = useState<string>("");
  const kanaEditedRef = useRef(false); // true: 読み(カナ)がユーザー手動編集済み
  const [isProcessing, setIsProcessing] = useAtom(isProcessingAtom);
  const [, setIsThinking] = useAtom(isThinkingAtom);
  const [, setIsTranscribing] = useAtom(isTranscribingAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [volume] = useAtom(volumeAtom);
  const { t } = useTranslation();

  useEffect(() => {
    if (mode === "edit" && initialDict) {
      setWord(initialDict.surface);
      setKana(initialDict.pronunciation);
      setPriority(Number(initialDict.priority));
      setAccent(Number(initialDict.accent_type));
      setTargetId(initialDict.id);
    } else if (mode === "create" && !initialDict) {
      reset();
    }
  }, [mode, initialDict]);

  const moras = splitMoras(kana);
  useEffect(() => {
    if (accent > moras.length) setAccent(moras.length || 1);
  }, [kana, moras.length, accent]);

  const getSpeakerInfo = () => {
    let speakerInfo = { ...defaultSpeakerInfo };
    if (getVRMName(currentVRM) === "Loppy") {
      speakerInfo = LoppyInfo;
    } else {
      speakerInfo.id = [currentSpeaker.Id, currentSpeaker.Id];
    }
    return speakerInfo;
  };

  const buildDict = (pronunOverride?: string): Dict => ({
    surface: word.trim(),
    pronunciation: (pronunOverride ?? kana).trim(),
    accent_type: accent,
    priority,
  });

  // 登録済み辞書との重複チェック
  const isDuplicateSurface = (surface: string): boolean => {
    const trimmed = surface.trim();
    return registeredDicts.some((d) => d.surface === trimmed && d.id !== targetId);
  };

  /**
   * プレビュー再生処理
   * 入力値の検証を行い、音声合成を実行する
   * アクセント明確化のため「が」を付与して合成する
   */
  const preview = useCallback(async () => {
    PlaySound("push", volume);
    // CV設定がVOICEVOX以外の場合は通知トーストを表示
    if (T2SEngine !== t2SEngines.voicevox) {
      // 2025.08現在、管理者画面は日本語想定のみ、かつ近々辞書の対応範囲も拡大する予定という理由から、日本語のハードコーディングで対応
      showNotificationDialog(
        "辞書機能は現在VOICEVOXのみ対応しています。\nプレビュー再生する際は、音声設定 > CV選択 から「VOICEVOX」に切り替えてください。",
        "warning"
      );
      return;
    }

    const [ok, msg] = validateInputs(word, kana, t);
    if (!ok) {
      showNotificationDialog(msg, "warning");
      return;
    }
    if (isDuplicateSurface(word)) {
      showNotificationDialog(t("duplicateDictionaryWord"), "warning");
      return;
    }
    setIsProcessing(true);
    const d = buildDict();
    const speakerInfo = getSpeakerInfo();

    try {
      if (mode === "create") {
        const customerId = await getCurrentCustomerId();
        const uuid = await addDictionary(d, customerId);
        if (!uuid) {
          showNotificationDialog(t("audioPlaybackErrorTitle"), "error");
          return;
        }
        const text = word + "が";
        await handleSynthesize(
          text,
          setIsThinking,
          setIsProcessing,
          setIsTranscribing,
          () => {},
          speakerInfo
        );
        await deleteDictionary(uuid, customerId);
      } else {
        // 編集モード: UI上の値で一時的に更新し、合成後に元に戻す
        const customerId = await getCurrentCustomerId();
        await updateDictionary(targetId, d, customerId);
        const text = word + "が";
        try {
          await handleSynthesize(
            text,
            setIsThinking,
            setIsProcessing,
            setIsTranscribing,
            () => {},
            speakerInfo
          );
        } finally {
          // 元の値に復元
          if (initialDict) {
            await updateDictionary(
              targetId,
              {
                surface: initialDict.surface,
                pronunciation: initialDict.pronunciation,
                accent_type: Number(initialDict.accent_type),
                priority: Number(initialDict.priority),
              },
              customerId
            );
          }
        }
      }
    } catch {
      showNotificationDialog(t("audioPlaybackErrorTitle"), "error");
    } finally {
      setIsProcessing(false);
    }
  }, [mode, word, kana, accent, priority, currentSpeaker.Id, currentVRM, T2SEngine, volume]);

  /**
   * 保存処理
   * 入力値の検証を行い、問題なければ辞書に追加または更新する
   * create: 保存後に初期化
   * edit: 保存後にモーダルを閉じる
   */
  // eslint-disable-next-line complexity
  const save = async () => {
    PlaySound("push", volume);
    const [ok, msg] = validateInputs(word, kana, t);
    if (!ok) {
      showNotificationDialog(msg, "warning");
      return;
    }
    const d = buildDict();
    if (isDuplicateSurface(word)) {
      showNotificationDialog(t("duplicateDictionaryWord"), "warning");
      return;
    }
    try {
      const customerId = await getCurrentCustomerId();
      if (mode === "create") {
        const result = await addDictionary(d, customerId);
        if (!result) {
          showNotificationDialog(t("errorOccurred"), "error");
          return;
        }
        showNotificationDialog(t("dictionaryRegistered"), "success");
      } else if (mode === "edit" && targetId) {
        const result = await updateDictionary(targetId, d, customerId);
        if (!result) {
          showNotificationDialog(t("errorOccurred"), "error");
          return;
        }
        showNotificationDialog(t("dictionaryUpdated"), "success");
      }
      if (onSaved) onSaved();
      if (mode === "create") reset();
    } catch {
      showNotificationDialog(t("audioPlaybackErrorTitle"), "error");
    }
  };

  /**
   * 削除処理
   * 削除確認を行い、ユーザーが確認した場合に辞書を削除する
   */
  const remove = async () => {
    if (mode !== "edit" || !targetId) return;
    if (!window.confirm(`「${word}」${t("deleteConfirmation")}`)) return;
    PlaySound("push", volume);
    try {
      const customerId = await getCurrentCustomerId();
      const result = await deleteDictionary(targetId, customerId);
      if (!result) {
        showNotificationDialog(t("errorOccurred"), "error");
        return;
      }
      showNotificationDialog(t("dictionaryDeleted"), "success");
      if (onDeleted) onDeleted();
    } catch {
      showNotificationDialog(t("audioPlaybackErrorTitle"), "error");
    }
  };

  // 初期化
  const reset = () => {
    setWord("");
    setKana("");
    setPriority(9);
    setAccent(1);
    setTargetId("");
    kanaEditedRef.current = false;
  };

  /**
   * キャンセル処理
   * create: 初期化
   * edit: モーダルを閉じる
   */
  const cancel = () => {
    PlaySound("push", volume);
    if (mode === "create") reset();
    if (onCancel) onCancel();
  };

  // 読み欄が空に戻ったら自動補完を再度許可
  useEffect(() => {
    if (kana === "") kanaEditedRef.current = false;
  }, [kana]);

  // VoiceVoxの型定義
  interface VVQueryMora {
    text: string;
  }
  interface VVQueryAccentPhrase {
    moras: VVQueryMora[];
  }
  interface VVAudioQuery {
    accent_phrases?: VVQueryAccentPhrase[];
  }

  /**
   * ひらがなをカタカナに変換
   * @param src ひらがな文字列
   * @returns カタカナ文字列
   */
  const toKatakana = (src: string) =>
    src.replace(/[ぁ-ん]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0x60));

  /**
   * 長音符(ー)を直前モーラの母音で展開する
   * @param src カタカナ文字列
   * @returns 長音符なしのカタカナ文字列
   */
  const expandLongVowels = (src: string): string => {
    if (!src || !src.includes("ー")) return src;
    const A = "アァャヤカガサザタダナハバパマラワァャヤ".split("");
    const I = "イィキギシジチヂニヒビピミリィ".split("");
    const U = "ウゥュユクグスズツヅヌフブプムルゥュユ".split("");
    const E = "エェケゲセゼテデネヘベペメレェ".split("");
    const O = "オォョヨコゴソゾトドノホボポモロヲォョヨ".split("");
    const smallMap: Record<string, string> = {
      ァ: "ア",
      ィ: "イ",
      ゥ: "ウ",
      ェ: "エ",
      ォ: "オ",
      ャ: "ア",
      ュ: "ウ",
      ョ: "オ",
    };
    const pickVowel = (ch: string): string | null => {
      if (smallMap[ch]) return smallMap[ch];
      if (A.includes(ch)) return "ア";
      if (I.includes(ch)) return "イ";
      if (U.includes(ch)) return "ウ";
      if (E.includes(ch)) return "エ";
      if (O.includes(ch)) return "オ";
      if (ch === "ン") return null; // 直前がンの場合は無視
      return null;
    };
    const chars = src.split("");
    let out = "";
    for (let i = 0; i < chars.length; i++) {
      const c = chars[i];
      if (c === "ー") {
        // 直前有効文字を探す
        let j = i - 1;
        let prev = "";
        while (j >= 0) {
          if (chars[j] !== "ー") {
            prev = chars[j];
            break;
          }
          j--;
        }
        if (prev) {
          const vowel = pickVowel(prev);
          if (vowel) {
            out += vowel;
            continue;
          }
        }
        // 判定不可の場合はスキップ(ー除去)
        continue;
      }
      out += c;
    }
    return out;
  };

  /**
   * VOICEVOXからカナを取得する
   * 言葉に漢字が含まれている場合のみ実行する
   * @param text 言葉
   */
  const fetchKanaFromVoicevox = async (text: string): Promise<string | null> => {
    // カナ取得だけであればspeakerは何でも良いため、Atom.tsxのcurrentSpeakerAtomの初期値でAPIコール
    const speakerId = 2; // 四国めたん(ノーマル)
    try {
      const body = (await api.voicevox.audioQuery.mutate({
        speaker: speakerId,
        text,
      })) as VVAudioQuery;
      if (!body?.accent_phrases) return null;
      const hira = body.accent_phrases.flatMap((p) => (p.moras || []).map((m) => m.text)).join("");
      if (!hira) return null;
      return expandLongVowels(toKatakana(hira));
    } catch {
      return null;
    }
  };

  /**
   * 自動カナ生成
   */
  const suggestKana = async () => {
    if (kanaEditedRef.current || !word || (kana && kana.trim().length > 0)) return;
    // 1. 漢字を含んでいる場合、VOICEVOXで取得
    if (/[一-龥々]/.test(word)) {
      const fetched = await fetchKanaFromVoicevox(word);
      if (fetched) {
        setKana(fetched);
        return;
      }
    }
    // 2. かなフォールバック
    const trimmed = word.replace(/\s/g, "");
    if (!/^[ぁ-んァ-ヶー・･ﾞﾟ]+$/.test(trimmed)) return;
    const auto = expandLongVowels(toKatakana(trimmed));
    if (auto !== kana) setKana(auto);
  };

  /**
   * フラグをカナ編集済みに設定
   */
  const markKanaEdited = () => {
    kanaEditedRef.current = true;
  };

  return {
    word,
    kana,
    priority,
    accent,
    moras,
    isProcessing,
    setWord,
    setKana,
    setPriority,
    setAccent,
    preview,
    save,
    remove,
    cancel,
    reset,
    suggestKana,
    markKanaEdited,
  };
};

export type UseDictionaryReturn = ReturnType<typeof useDictionary>;
